import { useCallback, useEffect, useMemo, useState } from 'react';

import { fetchTarifs, fetchTarifsFilieresOverrides } from '../services/academiaAdminService';
import {
  buildBrevetPricingHint,
  fallbackPublicTarifCategories,
  groupPublicTarifsByCategory,
  type PublicTarifCategoryDisplay,
  type TarifFormationRow,
} from '../utils/academiaTarifs';
import { mergeTarifsWithOverrides } from '../utils/filiereTarifsDraft';

export function usePublicTarifs(idFiliere?: string | null) {
  const [rows, setRows] = useState<TarifFormationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    setLoading(true);
    try {
      const data = await fetchTarifs(idFiliere || undefined);
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setError(true);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [idFiliere]);

  useEffect(() => {
    void load();
  }, [load]);

  const categories: PublicTarifCategoryDisplay[] = useMemo(() => {
    if (rows.length > 0) return groupPublicTarifsByCategory(rows);
    return fallbackPublicTarifCategories();
  }, [rows]);

  const brevetHint = useMemo(() => {
    if (rows.length > 0) return buildBrevetPricingHint(rows);
    return 'Le brevet certifiant est facturé séparément des frais ci-dessous : équivalent 50 USD, converti en CDF au taux du jour.';
  }, [rows]);

  return { rows, categories, brevetHint, loading, error, reload: load };
}

/** Tarifs globaux + overrides par filière (pour cartes programmes d’un domaine). */
export function usePublicTarifsWithFiliereOverrides() {
  const [globalRows, setGlobalRows] = useState<TarifFormationRow[]>([]);
  const [overrides, setOverrides] = useState<
    Array<{
      idFiliere: string;
      categorie: string;
      typeFrais: string;
      montantCdf?: number | string;
      referenceUsd?: number | string | null;
    }>
  >([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const [glob, ov] = await Promise.all([fetchTarifs(), fetchTarifsFilieresOverrides()]);
        if (cancelled) return;
        setGlobalRows(Array.isArray(glob) ? glob : []);
        setOverrides(Array.isArray(ov) ? ov : []);
      } catch {
        if (!cancelled) {
          setGlobalRows([]);
          setOverrides([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const categoriesForFiliere = useCallback(
    (idFiliere: string): PublicTarifCategoryDisplay[] => {
      const merged = mergeTarifsWithOverrides(globalRows, overrides, idFiliere);
      if (merged.length > 0) return groupPublicTarifsByCategory(merged as TarifFormationRow[]);
      return fallbackPublicTarifCategories();
    },
    [globalRows, overrides],
  );

  const defaultCategories = useMemo(() => {
    if (globalRows.length > 0) return groupPublicTarifsByCategory(globalRows);
    return fallbackPublicTarifCategories();
  }, [globalRows]);

  return { loading, defaultCategories, categoriesForFiliere, globalRows, overrides };
}
